// CategoryManager.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  IconButton,
  Button,
  Portal,
  Collapse,
  ListItemButton
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import {
  User,
  Briefcase,
  Users,
  Plus,
  MinusCircle,
  Puzzle,
  X,
  Check,
  ChevronRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

import {
  getUserCategories,
  createUserCategory,
  deleteUserCategory
} from '../../AGTHall/services/AGTHallService';

// Contenedor principal alineado con ProjectManager
const CategoriesContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  boxSizing: 'border-box',
  padding: '0px',
}));

// Header de sección cambiado a ListItemButton para que sea totalmente clickeable
const CategorySectionHeader = styled(ListItemButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    margin: '4px 0',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? alpha('#FFFFFF', 0.05)
        : alpha(
          theme.palette.mode === 'dark' ? '#2C2C2E' : '#F2F2F7',
          0.8
        ),
    },
    cursor: 'pointer',
  };
});

const CategoryHeading = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '16px',
    fontWeight: 600,
    color: isWallpaperMode ? '#FFFFFF' : theme.palette.text.primary,
  };
});

// New styled components for the list-style layout with updated styles
const CategoryItem = styled(Box, {
  shouldForwardProp: (prop) => !['selected', 'isAdd'].includes(prop),
})(({ theme, selected, isAdd }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: isAdd
      ? 'transparent'
      : isWallpaperMode
        ? 'rgba(255, 255, 255, 0.05)'
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F7'),
    borderRadius: '28px',
    padding: '10px 16px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: selected
      ? isWallpaperMode
        ? `2px solid #FFFFFF`
        : `2px solid ${theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'}`
      : isAdd
        ? isWallpaperMode
          ? `2px dashed rgba(255, 255, 255, 0.3)`
          : `2px dashed ${theme.palette.mode === 'dark' ? 'rgba(3, 133, 255, 0.3)' : 'rgba(0, 123, 254, 0.3)'}`
        : `2px solid transparent`,
    '&:hover': {
      backgroundColor: isAdd
        ? 'transparent'
        : isWallpaperMode
          ? 'rgba(255, 255, 255, 0.1)'
          : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB'),
    },
    boxSizing: 'border-box',
  };
});

const CategoryIcon = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    flexShrink: 0,
    '& svg': {
      color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
      width: '14px',
      height: '14px',
    }
  };
});

const CategoryText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flex: 1,
    fontSize: '14px',
    fontWeight: selected ? 600 : 500,
    color: isWallpaperMode ? '#FFFFFF' : theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
});

const CheckmarkIcon = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      color: isWallpaperMode ? '#000000' : '#FFFFFF',
      width: '14px',
      height: '14px',
    }
  };
});

// Original styled components maintained for dialogs
const ModalContent = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#2D2D2D' : '#ffffff'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '560px',
    position: 'relative',
    minHeight: '300px',
    display: 'flex',
    flexDirection: 'column',
    color: isWallpaperMode ? '#FFFFFF' : 'inherit',
    transition: 'all 300ms ease',
  };
});

const CloseButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    right: '16px',
    top: '16px',
    padding: '4px',
    color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
  };
});

const BlurBackdrop = ({ open }) => {
  const theme = useTheme();

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha('#000000', 0.3),
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: theme.zIndex.modal - 1,
          opacity: open ? 1 : 0,
          transition: 'opacity 300ms ease',
          visibility: open ? 'visible' : 'hidden',
        }}
      />
    </Portal>
  );
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  return {
    '& .MuiDialog-paper': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      margin: '24px',
      maxWidth: '560px',
      width: '100%',
      borderRadius: '24px',
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'transparent',
    },
  };
});

const StyledInputModal = styled('input')(({ theme, error }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    padding: '16px',
    borderRadius: '12px',
    border: `1px solid ${
      error ? '#ff1744'
      : isWallpaperMode
        ? 'rgba(255, 255, 255, 0.3)'
        : (theme.palette.mode === 'dark'
          ? '#404040'
          : '#e0e0e0')
    }`,
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f7'),
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#000000'),
    fontSize: '16px',
    outline: 'none',
    transition: 'all 0.2s',
    marginTop: '16px',
    '&:focus': {
      borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
      boxShadow: isWallpaperMode
        ? '0 0 0 4px rgba(255, 255, 255, 0.15)'
        : '0 0 0 4px rgba(3, 133, 255, 0.15)',
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.15)'
        : (theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff'),
    },
    '&::placeholder': {
      color: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.5)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.5)'
          : 'rgba(0, 0, 0, 0.4)'),
    },
  };
});

const ActionButton = styled(Button)(({ theme, variant }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '24px',
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '15px',
    transition: 'all 0.2s',
    ...(variant === 'cancel' && {
      color: isWallpaperMode
        ? '#FFFFFF'
        : (theme.palette.mode === 'dark' ? '#ff453a' : '#ff3b30'),
      backgroundColor: 'transparent',
      border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.1)'
          : (theme.palette.mode === 'dark'
            ? 'rgba(255, 69, 58, 0.1)'
            : 'rgba(255, 59, 48, 0.1)'),
      },
    }),
    ...(variant === 'contained' && {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
      color: isWallpaperMode ? '#000000' : '#ffffff',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.85)'
          : '#0374e0',
      },
    }),
    ...(variant === 'delete' && {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : '#ff3b30',
      color: isWallpaperMode ? '#000000' : '#ffffff',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.85)'
          : '#ff1e0f',
      },
    }),
  };
});

const CategoryManager = ({ 
  userId, 
  selectedCategory, 
  onCategoryChange,
  onNotification,
  onModalOpenChange
}) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Inicializar hook de traducción
  
  // States
  const [customCategories, setCustomCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);

  useEffect(() => {
    if (onModalOpenChange) {
      onModalOpenChange(showCategoryModal || showDeleteCategoryModal);
    }
  }, [onModalOpenChange, showCategoryModal, showDeleteCategoryModal]);

  // Load custom categories
  useEffect(() => {
    if (userId) {
      getUserCategories(userId)
        .then((cats) => setCustomCategories(cats))
        .catch((err) => {
          console.error('[CategoryManager] Error fetching custom categories:', err);
          onNotification && onNotification({
            message: t('categoryManager.fetchError'),
            severity: 'error'
          });
        });
    }
  }, [userId, onNotification, t]);

  // Toggle Categories expanded state
  const toggleCategoriesExpanded = useCallback(() => {
    setIsCategoriesExpanded(prev => !prev);
  }, []);

  // Handlers
  const handleCategoryClick = useCallback(
    (category) => {
      if (category === 'add') {
        setShowCategoryModal(true);
        return;
      }
      onCategoryChange(category);
    },
    [onCategoryChange]
  );

  const handleDeleteCategoryRequest = useCallback((catName) => {
    setCategoryToDelete(catName);
    setShowDeleteCategoryModal(true);
  }, []);

  const handleConfirmDeleteCategory = useCallback(async () => {
    if (!categoryToDelete || !userId) return;
    setShowDeleteCategoryModal(false);

    try {
      const updatedCategories = await deleteUserCategory(
        userId,
        categoryToDelete,
        'personal'
      );
      setCustomCategories(updatedCategories);

      if (selectedCategory === categoryToDelete) {
        onCategoryChange('personal');
      }

      onNotification && onNotification({
        message: `${t('categoryManager.deleteTitle')}: "${categoryToDelete}"`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting category', err);
      onNotification && onNotification({
        message: t('categoryManager.fetchError'),
        severity: 'error'
      });
    } finally {
      setCategoryToDelete(null);
    }
  }, [userId, categoryToDelete, selectedCategory, onCategoryChange, onNotification, t]);

  const handleCancelDeleteCategory = useCallback(() => {
    setShowDeleteCategoryModal(false);
    setCategoryToDelete(null);
  }, []);

  return (
    <CategoriesContainer>
      {/* Categories header section with toggle - convertido a ListItemButton */}
      <CategorySectionHeader onClick={toggleCategoriesExpanded} disableRipple={false}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flex: '1 1 auto',
          minWidth: 0, // Permite que el contenido se contraiga si es necesario
        }}>
          <ChevronRight
            size={20}
            style={{
              transform: isCategoriesExpanded ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.25s ease-in-out',
              color: theme.palette.actualMode === 'wallpaper'
                ? '#FFFFFF'
                : theme.palette.text.secondary,
              marginRight: '8px',
              flexShrink: 0,
            }}
          />
          <CategoryHeading sx={{ flexShrink: 1, minWidth: 0 }}>
            {t('categoryManager.title')} {customCategories.length > 0 && `(${customCategories.length + 3})`}
          </CategoryHeading>
        </Box>

        {customCategories.length > 0 && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setShowCategoryManager(!showCategoryManager);
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: '6px 12px',
              marginLeft: '12px',
              borderRadius: '16px',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(3, 133, 255, 0.1)'
                    : 'rgba(3, 133, 255, 0.08)'),
              },
              '&:active': {
                backgroundColor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(3, 133, 255, 0.15)'
                    : 'rgba(3, 133, 255, 0.12)'),
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
                fontSize: '14px',
                fontWeight: 500,
                userSelect: 'none',
              }}
            >
              {showCategoryManager ? t('categoryManager.done') : t('categoryManager.edit')}
            </Typography>
          </Box>
        )}
      </CategorySectionHeader>

      {/* Collapsible Categories content */}
      <Collapse in={isCategoriesExpanded} timeout="auto" sx={{ width: '100%' }}>
        <Box sx={{ mt: 1, px: 1.5 }}>
          {/* Personal Category */}
          <CategoryItem 
            selected={selectedCategory === 'personal'} 
            onClick={() => handleCategoryClick('personal')}
          >
            <CategoryIcon>
              <User />
            </CategoryIcon>
            <CategoryText selected={selectedCategory === 'personal'}>
              {t('categoryManager.personal')}
            </CategoryText>
            {selectedCategory === 'personal' && (
              <CheckmarkIcon>
                <Check />
              </CheckmarkIcon>
            )}
          </CategoryItem>
          
          {/* Work Category */}
          <CategoryItem 
            selected={selectedCategory === 'work'} 
            onClick={() => handleCategoryClick('work')}
          >
            <CategoryIcon>
              <Briefcase />
            </CategoryIcon>
            <CategoryText selected={selectedCategory === 'work'}>
              {t('categoryManager.work')}
            </CategoryText>
            {selectedCategory === 'work' && (
              <CheckmarkIcon>
                <Check />
              </CheckmarkIcon>
            )}
          </CategoryItem>
          
          {/* Team Category */}
          <CategoryItem 
            selected={selectedCategory === 'team'} 
            onClick={() => handleCategoryClick('team')}
          >
            <CategoryIcon>
              <Users />
            </CategoryIcon>
            <CategoryText selected={selectedCategory === 'team'}>
              {t('categoryManager.team')}
            </CategoryText>
            {selectedCategory === 'team' && (
              <CheckmarkIcon>
                <Check />
              </CheckmarkIcon>
            )}
          </CategoryItem>
          
          {/* Custom Categories */}
          {customCategories.map((cat) => (
            <CategoryItem
              key={cat.name}
              selected={selectedCategory === cat.name}
              onClick={() => !showCategoryManager ? handleCategoryClick(cat.name) : null}
            >
              <CategoryIcon>
                <Puzzle />
              </CategoryIcon>
              <CategoryText selected={selectedCategory === cat.name}>
                {cat.name}
              </CategoryText>
              {showCategoryManager ? (
                <IconButton 
                  size="small" 
                  sx={{ color: '#FF3B30', width: '24px', height: '24px', padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCategoryRequest(cat.name);
                  }}
                >
                  <MinusCircle size={16} />
                </IconButton>
              ) : (
                selectedCategory === cat.name && (
                  <CheckmarkIcon>
                    <Check />
                  </CheckmarkIcon>
                )
              )}
            </CategoryItem>
          ))}
          
          {/* Add Category Button */}
          {!showCategoryManager && customCategories.length < 2 && (
            <CategoryItem 
              isAdd={true}
              onClick={() => handleCategoryClick('add')}
            >
              <CategoryIcon sx={{ 
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(52, 199, 89, 0.12)' : 'rgba(52, 199, 89, 0.08)'
              }}>
                <Plus size={14} color="#34C759" />
              </CategoryIcon>
              <CategoryText sx={{ color: '#34C759', fontSize: '14px' }}>
                {t('categoryManager.addNew')}
              </CategoryText>
            </CategoryItem>
          )}
        </Box>
      </Collapse>

      {/* MODAL for CREATING new category */}
      <BlurBackdrop open={showCategoryModal} />
      <StyledDialog
        open={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        maxWidth="sm"
        fullWidth
        closeAfterTransition
        BackdropProps={{
          invisible: true,
        }}
      >
        <ModalContent>
          <CloseButton onClick={() => setShowCategoryModal(false)}>
            <X size={20} />
          </CloseButton>

          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              fontSize: '24px',
              letterSpacing: '-0.5px',
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit'
            }}
          >
            {t('categoryManager.createTitle')}
          </Typography>

          <StyledInputModal
            placeholder={t('categoryManager.placeholder')}
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            autoFocus
          />

          <Typography sx={{
            mt: 3,
            color: theme => theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.7)'
              : (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.7)'
                : 'rgba(0, 0, 0, 0.6)'),
            fontSize: '14px'
          }}>
            {t('categoryManager.createDescription')}
          </Typography>

          <Box
            sx={{
              mt: 'auto',
              pt: 4,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <ActionButton 
              variant="cancel"
              onClick={() => setShowCategoryModal(false)}
            >
              {t('categoryManager.cancel')}
            </ActionButton>
            
            <ActionButton
              variant="contained"
              onClick={async () => {
                if (!newCategoryName.trim()) return;
                try {
                  const updatedCategories = await createUserCategory(
                    userId,
                    newCategoryName.trim(),
                    'puzzle'
                  );
                  setCustomCategories(updatedCategories);
                  setNewCategoryName('');
                  setShowCategoryModal(false);
                } catch (err) {
                  console.error(err);
                  onNotification && onNotification({
                    message: t('categoryManager.createError'),
                    severity: 'error'
                  });
                }
              }}
            >
              {t('categoryManager.create')}
            </ActionButton>
          </Box>
        </ModalContent>
      </StyledDialog>

      {/* Confirmation MODAL for deleting category */}
      <BlurBackdrop open={showDeleteCategoryModal} />
      <StyledDialog
        open={showDeleteCategoryModal}
        onClose={handleCancelDeleteCategory}
        maxWidth="sm"
        fullWidth
        closeAfterTransition
        BackdropProps={{
          invisible: true,
        }}
      >
        <ModalContent>
          <CloseButton onClick={handleCancelDeleteCategory}>
            <X size={20} />
          </CloseButton>

          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              fontSize: '24px',
              letterSpacing: '-0.5px',
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit'
            }}
          >
            {t('categoryManager.deleteTitle')}
          </Typography>

          <Typography sx={{
            fontSize: '15px',
            color: theme => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit'
          }}>
            {t('categoryManager.deleteConfirmation')} <strong>{categoryToDelete}</strong>?
          </Typography>

          <Typography
            sx={{
              mt: 2,
              color: theme => theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.7)'
                : (theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'rgba(0, 0, 0, 0.6)'),
              fontSize: '14px'
            }}
          >
            {t('categoryManager.deleteNote')}
          </Typography>

          <Box
            sx={{
              mt: 'auto',
              pt: 4,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <ActionButton 
              variant="cancel"
              onClick={handleCancelDeleteCategory}
            >
              {t('categoryManager.cancel')}
            </ActionButton>
            
            <ActionButton
              variant="delete"
              onClick={handleConfirmDeleteCategory}
            >
              {t('categoryManager.delete')}
            </ActionButton>
          </Box>
        </ModalContent>
      </StyledDialog>
    </CategoriesContainer>
  );
};

export default CategoryManager;
